import React, { useState, useEffect } from 'react';
import { RefreshCw, Sparkles, X, ArrowRight, ShieldCheck } from 'lucide-react';
import { registerUpdateListener, applyPWAUpdate, checkForPWAUpdate } from '../utils/pwaUpdateService';

export function PWAUpdateBanner() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const unsubscribe = registerUpdateListener((updateReady) => {
      if (updateReady) {
        setHasUpdate(true);
        setIsDismissed(false); // Re-show if a fresh update arrives
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  if (!hasUpdate || isDismissed) {
    return null;
  }

  const handleUpdate = () => {
    setIsUpdating(true);
    applyPWAUpdate();
  };

  return (
    <div
      id="pwa-update-banner"
      className="fixed top-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-[100] bg-[#111416]/95 backdrop-blur-xl border border-[#14B8A6]/50 shadow-2xl rounded-2xl p-4 text-white animate-in slide-in-from-top-4 fade-in duration-300 ring-1 ring-[#14B8A6]/20"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#14B8A6]/15 border border-[#14B8A6]/30 text-[#14B8A6] flex items-center justify-center shrink-0 mt-0.5">
            <RefreshCw className={isUpdating ? 'animate-spin' : ''} size={18} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-white tracking-wide">AzuraOS Update Ready</span>
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#14B8A6]/20 text-[#14B8A6] text-[9px] font-extrabold uppercase">
                <Sparkles size={9} />
                New Release
              </span>
            </div>
            <p className="text-[11px] text-gray-300 mt-1 leading-relaxed">
              A newer version of your sovereign operating system is downloaded and ready to apply.
            </p>
          </div>
        </div>

        <button
          id="btn-dismiss-update-banner"
          onClick={() => setIsDismissed(true)}
          className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-[#1E2325] transition-colors shrink-0 cursor-pointer"
          title="Dismiss for this session"
        >
          <X size={14} />
        </button>
      </div>

      <div className="mt-3.5 flex items-center gap-2 pt-2 border-t border-[#23292C]">
        <button
          id="btn-apply-pwa-update"
          onClick={handleUpdate}
          disabled={isUpdating}
          className="flex-1 py-2 px-3.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold text-xs rounded-xl shadow-lg hover:shadow-[#14B8A6]/20 transition-all flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
        >
          <RefreshCw size={13} className={isUpdating ? 'animate-spin' : ''} />
          <span>{isUpdating ? 'Updating AzuraOS...' : 'Update & Restart'}</span>
        </button>
        <button
          id="btn-later-pwa-update"
          onClick={() => setIsDismissed(true)}
          className="py-2 px-3 bg-[#1C2124] hover:bg-[#252C30] text-gray-300 hover:text-white font-medium text-xs rounded-xl transition-all cursor-pointer"
        >
          Later
        </button>
      </div>
    </div>
  );
}

/**
 * Compact Check for Updates button that can be embedded in Settings or System Modals
 */
export function PWACheckUpdatesButton({ className = '' }: { className?: string }) {
  const [status, setStatus] = useState<'idle' | 'checking' | 'latest' | 'update_ready'>('idle');

  const handleManualCheck = async () => {
    setStatus('checking');
    try {
      const hasUpdate = await checkForPWAUpdate();
      if (hasUpdate) {
        setStatus('update_ready');
      } else {
        setStatus('latest');
        setTimeout(() => setStatus('idle'), 4000);
      }
    } catch {
      setStatus('latest');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <button
      id="btn-check-for-pwa-updates"
      onClick={status === 'update_ready' ? applyPWAUpdate : handleManualCheck}
      disabled={status === 'checking'}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer border ${
        status === 'update_ready'
          ? 'bg-[#14B8A6] text-black border-[#14B8A6] hover:bg-[#0EA5A4]'
          : 'bg-[#181C1E] text-gray-300 border-[#2A3135] hover:border-[#14B8A6]/50 hover:text-white'
      } ${className}`}
    >
      <RefreshCw size={12} className={status === 'checking' ? 'animate-spin text-[#14B8A6]' : ''} />
      <span>
        {status === 'checking' && 'Checking for updates...'}
        {status === 'latest' && 'Up to date (Latest)'}
        {status === 'update_ready' && 'Update Ready — Click to Apply'}
        {status === 'idle' && 'Check for updates'}
      </span>
    </button>
  );
}
